import {
	type ActionFunctionArgs,
	type MetaFunction,
	json,
} from '@remix-run/node'
import { useActionData, Form, useNavigation } from '@remix-run/react'
import { useEffect, useState } from 'react'
import { HoneypotInputs } from 'remix-utils/honeypot/react'
import { Input } from '#app/components/ui/input.tsx'
import { Textarea } from '#app/components/ui/textarea.tsx'
import { checkHoneypot } from '#app/utils/honeypot.server.ts'

export const meta: MetaFunction = () => [
	{ title: 'Contact - Barry McGee' },
	{
		name: 'description',
		content:
			'Get in touch with Barry McGee. Send a message for inquiries, collaboration, or just to say hello.',
	},
	{ property: 'og:title', content: 'Contact - Barry McGee' },
	{
		property: 'og:description',
		content:
			'Get in touch with Barry McGee. Send a message for inquiries, collaboration, or just to say hello.',
	},
	{ property: 'og:image', content: 'https://barrymcgee.dev/img/speaking.jpeg' },
	{ property: 'og:type', content: 'website' },
	{ property: 'og:url', content: 'https://barrymcgee.dev/contact' },
	{ name: 'twitter:card', content: 'summary_large_image' },
	{ name: 'twitter:site', content: '@barrymcgee' },
	{ name: 'twitter:creator', content: '@barrymcgee' },
]

export const action = async ({ request }: ActionFunctionArgs) => {
	const formData = await request.formData()
	checkHoneypot(formData)

	const name = formData.get('name')
	const email = formData.get('email')
	const message = formData.get('message')

	const response = await fetch('https://formcarry.com/s/b16irULpE-g', {
		method: 'POST',
		headers: { 'Content-Type': 'application/json' },
		body: JSON.stringify({ name, email, message }),
	})

	return json({ success: response.ok })
}

const channels: Array<{ label: string; value: string; href: string }> = [
	{
		label: 'Email',
		value: 'hello@barrymcgee.co.uk',
		href: 'mailto:hello@barrymcgee.co.uk',
	},
	{
		label: 'LinkedIn',
		value: '/in/barrymcgee',
		href: 'https://linkedin.com/in/barrymcgee',
	},
	{
		label: 'GitHub',
		value: '@barrymcgee',
		href: 'https://github.com/barrymcgee',
	},
	{
		label: 'Bluesky',
		value: '@barrymcgee.dev',
		href: 'https://bsky.app/profile/barrymcgee.dev',
	},
]

export default function Contact() {
	const actionData = useActionData<typeof action>()
	const [formStatus, setFormStatus] = useState<null | 'success' | 'error'>(null)
	const navigation = useNavigation()
	const submitting = navigation.state === 'submitting'

	useEffect(() => {
		if (actionData) {
			setFormStatus(actionData.success ? 'success' : 'error')
		}
	}, [actionData])

	if (formStatus === 'success') {
		return <SuccessState />
	}

	return (
		<div className="py-6 md:py-10">
			<header className="mb-10 md:mb-14">
				<div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
					Get in touch
				</div>
				<h1 className="mt-3 text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl">
					Drop me a line.
				</h1>
				<p className="mt-5 max-w-2xl text-base leading-relaxed text-muted-foreground md:text-lg">
					Happy to talk about agentic engineering, advisory work, speaking, or
					anything else you think I’ll find interesting.
				</p>
			</header>

			<div className="grid gap-12 md:grid-cols-[1fr_240px] md:gap-16">
				<Form method="post" className="space-y-5">
					<Field label="Your name" name="name" type="text" />
					<Field label="Email" name="email" type="email" />
					<Field label="What’s on your mind?" name="message" textarea />
					<HoneypotInputs />
					<div className="pt-2">
						<button
							type="submit"
							disabled={submitting}
							className="inline-flex items-center gap-2 rounded-full bg-foreground px-6 py-3 font-medium text-background transition-all hover:-translate-y-0.5 hover:opacity-90 disabled:translate-y-0 disabled:cursor-not-allowed disabled:opacity-60"
						>
							{submitting ? 'Sending…' : 'Send'}
							{!submitting ? <span aria-hidden>→</span> : null}
						</button>
						{formStatus === 'error' ? (
							<p className="mt-4 text-sm text-foreground/80">
								Something went wrong. Try again, or email me directly at{' '}
								<a
									// eslint-disable-next-line remix-react-routes/use-link-for-routes
									href="mailto:hello@barrymcgee.co.uk"
									className="underline underline-offset-2"
								>
									hello@barrymcgee.co.uk
								</a>
								.
							</p>
						) : null}
					</div>
				</Form>

				<aside>
					<div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
						Or find me elsewhere
					</div>
					<ul className="mt-4 list-none space-y-1 pl-0">
						{channels.map(c => (
							<li key={c.label}>
								<a
									href={c.href}
									target={c.href.startsWith('http') ? '_blank' : undefined}
									rel={
										c.href.startsWith('http')
											? 'noreferrer noopener'
											: undefined
									}
									className="group block rounded-lg border border-transparent px-3 py-2.5 text-foreground no-underline transition-colors hover:border-foreground/10 hover:bg-foreground/[0.03]"
								>
									<div className="font-medium">{c.label}</div>
									<div className="font-mono text-xs text-muted-foreground transition-colors group-hover:text-foreground">
										{c.value}
									</div>
								</a>
							</li>
						))}
					</ul>

					<div className="mt-10 rounded-2xl border border-foreground/10 bg-foreground/[0.02] p-5">
						<div className="font-mono text-xs uppercase tracking-wider text-muted-foreground">
							Response time
						</div>
						<p className="mt-2 text-sm leading-relaxed text-foreground">
							Usually a day or two during the week .. but maybe sooner!
						</p>
					</div>
				</aside>
			</div>
		</div>
	)
}

function Field({
	label,
	name,
	type = 'text',
	textarea,
}: {
	label: string
	name: string
	type?: string
	textarea?: boolean
}) {
	return (
		<div>
			<label
				htmlFor={name}
				className="mb-2 block max-w-none font-mono text-xs uppercase tracking-wider text-muted-foreground"
			>
				{label}
			</label>
			{textarea ? (
				<Textarea id={name} name={name} required className="min-h-[160px]" />
			) : (
				<Input id={name} name={name} type={type} required />
			)}
		</div>
	)
}

function SuccessState() {
	return (
		<div className="py-16 md:py-24">
			<div className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-emerald-700">
				<span className="relative flex h-2 w-2">
					<span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-60" />
					<span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
				</span>
				Message sent
			</div>
			<h1 className="mt-4 text-4xl font-bold leading-tight tracking-tight text-foreground md:text-5xl">
				Speak soon.
			</h1>
			<p className="mt-5 max-w-xl text-base leading-relaxed text-muted-foreground md:text-lg">
				Got it. I’ll be back to you as soon as I can — usually within a day or
				two during the week.
			</p>
		</div>
	)
}
